import { ClockIcon, EnvelopeIcon, EyeIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import BrowseActionsScene from "@/components/domains/BrowseActionsScene";
import { dismissDiscoverEducation } from "@/utils/liveEvaluationSummary";

const BEATS = [
  {
    title: "Watch the live player",
    detail: "You’ll see the browser session as we move through pages.",
    Icon: EyeIcon,
  },
  {
    title: "Follow the status banner",
    detail: "It updates as we go from mapping → browsing → evaluating.",
    Icon: ClockIcon,
  },
  {
    title: "Wait for the email notification",
    detail: "When the first evaluation finishes, we email you — open issues will also be ready on this page.",
    Icon: EnvelopeIcon,
  },
] as const;

type Props = {
  open: boolean;
  onDismiss: () => void;
  userId?: string;
};

export default function DomainDiscoverEducationDialog({ open, onDismiss, userId }: Props) {
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setOverlayRoot(document.getElementById("layout-main-overlay"));
  }, []);

  useEffect(() => {
    if (!open || !overlayRoot || !userId) {
      return;
    }

    dismissDiscoverEducation(userId);
  }, [open, overlayRoot, userId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onDismiss();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onDismiss]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const main = document.querySelector("main[scroll-region]");
    if (!(main instanceof HTMLElement)) {
      return;
    }

    const scrollTop = main.scrollTop;
    const previousOverflow = main.style.overflowY;
    main.style.overflowY = "hidden";
    main.scrollTop = scrollTop;

    return () => {
      main.style.overflowY = previousOverflow;
      main.scrollTop = scrollTop;
    };
  }, [open]);

  if (!open || !overlayRoot) {
    return null;
  }

  return createPortal(
    <div
      className="pointer-events-auto absolute inset-0 z-50 flex items-center justify-center overflow-y-auto p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="discover-education-title"
    >
      <div className="absolute inset-0 bg-gray-900/20 backdrop-blur-sm" aria-hidden="true" />
      <div className="relative my-auto w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 sm:px-8 sm:py-5">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <MagnifyingGlassIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-primary">First evaluation</p>
              <h2
                id="discover-education-title"
                className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl"
              >
                About 10 minutes — here’s the plan
              </h2>
            </div>
          </div>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onDismiss} aria-label="Close">
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="grid sm:grid-cols-2">
          <div className="flex flex-col border-b border-gray-100 px-6 py-5 sm:border-b-0 sm:border-r sm:px-8 sm:py-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">What we’ll do</p>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">
                To find usability issues, we’ll browse your site like a real visitor: navigate pages, click through
                flows, and interact with forms and controls.
              </p>
            </div>
            <div className="mt-12 hidden justify-center sm:flex">
              <BrowseActionsScene />
            </div>
          </div>

          <div className="px-6 py-5 sm:px-8 sm:py-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">What you can do</p>
            <p className="mt-2 text-sm font-medium text-gray-800">Here’s what you can do while we work on that:</p>
            <ul className="mt-4 space-y-5">
              {BEATS.map((beat, index) => (
                <li key={beat.title} className="flex gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <beat.Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">
                      <span className="mr-1.5 text-primary">{index + 1}.</span>
                      {beat.title}
                    </p>
                    <p className="mt-0.5 text-sm text-gray-600">{beat.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-100 px-6 py-4 sm:px-8 sm:py-5">
          <button type="button" className="btn btn-primary w-full justify-center sm:w-auto" onClick={onDismiss}>
            Watch progress
          </button>
        </div>
      </div>
    </div>,
    overlayRoot,
  );
}
